/**
 * 🧩 Permission Groups Service - Integração com API de Grupos de Permissões
 * 
 * Service responsável por todas as chamadas relacionadas a grupos de permissões.
 * Integra com os endpoints criados em routes/permissionGroups.js do backend.
 * 
 * @author Pagmus Team
 * @date 2025-10-30
 */

import apiService from './index';

const PermissionGroupsService = {
  /**
   * 📊 Obter estatísticas de grupos do produtor
   * GET /api/permission-groups/stats
   */
  getStats: async () => {
    try {
      const response = await apiService.get('/api/permission-groups/stats');
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar estatísticas de grupos:', error);
      throw error;
    }
  },

  /**
   * 🧱 Listar blocos modulares do sistema
   * GET /api/permission-groups/system/blocks
   */
  getSystemBlocks: async () => {
    try {
      const response = await apiService.get('/api/permission-groups/system/blocks');
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar blocos do sistema:', error);
      throw error;
    }
  },

  /**
   * 🔗 Combinar permissões de múltiplos blocos
   * POST /api/permission-groups/system/combine-blocks
   * @param {Array<string>} blockTypes - Tipos dos blocos a combinar
   */
  combineBlocks: async (blockTypes) => {
    try {
      const response = await apiService.post('/api/permission-groups/system/combine-blocks', {
        block_types: blockTypes
      });
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao combinar blocos:', error);
      throw error;
    }
  },

  /**
   * 📋 Listar grupos de permissões do produtor
   * GET /api/permission-groups
   * @param {boolean} includeInactive - Incluir grupos inativos
   */
  getGroups: async (includeInactive = false) => {
    try {
      const params = includeInactive ? '?include_inactive=true' : '';
      const response = await apiService.get(`/api/permission-groups${params}`);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar grupos:', error);
      throw error;
    }
  },

  /**
   * 🔍 Buscar grupo específico por ID
   * GET /api/permission-groups/:groupId
   * @param {number} groupId - ID do grupo
   */
  getGroupById: async (groupId) => {
    try {
      const response = await apiService.get(`/api/permission-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar grupo:', error);
      throw error;
    }
  },

  /**
   * ✨ Criar novo grupo de permissões
   * POST /api/permission-groups
   * @param {Object} groupData - Dados do grupo
   * @param {string} groupData.name - Nome do grupo
   * @param {Object} groupData.permissions - Permissões do grupo
   * @param {string} groupData.description - Descrição (opcional)
   */
  createGroup: async (groupData) => {
    try {
      const response = await apiService.post('/api/permission-groups', groupData);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao criar grupo:', error);
      throw error;
    }
  },

  /**
   * 🔄 Atualizar grupo existente
   * PUT /api/permission-groups/:groupId
   * @param {number} groupId - ID do grupo
   * @param {Object} groupData - Dados a atualizar
   */
  updateGroup: async (groupId, groupData) => {
    try {
      const response = await apiService.put(`/api/permission-groups/${groupId}`, groupData);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao atualizar grupo:', error);
      throw error;
    }
  },

  /**
   * 🗑️ Deletar grupo (soft delete)
   * DELETE /api/permission-groups/:groupId
   * @param {number} groupId - ID do grupo
   */
  deleteGroup: async (groupId) => {
    try {
      const response = await apiService.delete(`/api/permission-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao deletar grupo:', error);
      throw error;
    }
  }
};

export default PermissionGroupsService;

